import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; 
import { collection, addDoc, updateDoc, deleteDoc, doc, setDoc, writeBatch } from 'firebase/firestore';
import { Lock, Printer, Search, Download, Shield, Power, Save, Eye, EyeOff, Check, Ban, FileText, Trash2, RefreshCw, Gift, X, UserPlus, AlertTriangle, Globe, ChevronDown, ChevronUp, DollarSign, Clock, Mail } from 'lucide-react';
import { BRANDING, getTodayFormatted, formatDate, getWeekNumber, getDayName, formatTimeSlot, parseTime } from '../utils/helpers';
import ClinicalHistoryModal from './ClinicalHistoryModal';

// --- PROTECCIÓN: Valores por defecto en props para evitar pantallas blancas ---
const AdminPanel = ({ 
    config = {}, 
    setConfig, 
    faqs = [], 
    setFaqs, 
    auth = {}, 
    setAuth, 
    appointments = [], 
    logs = [], 
    setLogs, 
    clinicalRecords = {}, 
    setClinicalRecords, 
    user, 
    setAdminAuth 
}) => {
  const [tab, setTab] = useState('citas');
  const [filterWeek, setFilterWeek] = useState('all');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPassword, setShowPassword] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFinance, setSearchFinance] = useState('');
  const [expandedSummary, setExpandedSummary] = useState(null);
  const [loadingRate, setLoadingRate] = useState(false);
  const [showManualBooking, setShowManualBooking] = useState(false);

  // Permisos: 'booking' suele ser Secretaria. 'finance' es Administración. 'all' es Doctor/SuperAdmin.
  const can = (perm) => user?.permissions?.includes('all') || user?.permissions?.includes(perm);

  useEffect(() => {
    if (config?.autoUpdateBCV && can('all') && !loadingRate) {
        fetchBCVRate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const toggleSummary = (week) => {
    setExpandedSummary(expandedSummary === week ? null : week);
  };

  const logAction = async (action, detail) => {
    const newLog = { date: new Date().toISOString(), user: user.label, action, detail };
    try { await addDoc(collection(db, "logs"), newLog); } catch(e) {}
  };

  // --- LÓGICA DE HUECOS (GAP FILLING) ---
  const findFirstAvailableSlot = (dateString, existingApps) => {
      const activeApps = existingApps.filter(a => a.date === dateString && a.status !== 'Cancelada');
      const takenTimes = activeApps.map(a => a.assignedTime);
      
      let slotIndex = 0;
      let foundTime = null;
      
      while (!foundTime && slotIndex < 50) {
          const theoreticallyTime = formatTimeSlot(slotIndex);
          if (!takenTimes.includes(theoreticallyTime)) {
              foundTime = theoreticallyTime;
          } else {
              slotIndex++;
          }
      }
      return foundTime || formatTimeSlot(activeApps.length);
  };

  // --- AGENDAMIENTO MANUAL (MEJORADO) ---
  const handleManualBooking = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    const appsOnDate = appointments.filter(a => a.date === data.date && a.status !== 'Cancelada');
    const count = appsOnDate.length;
    const limitRegular = parseInt(config?.dailyLimit) || 10;
    const limitExtra = parseInt(config?.extraLimit) || 2;
    const totalMax = limitRegular + limitExtra;

    let finalMotivo = data.motivo;
    let isExtra = false;

    // 1. Validaciones de Cupo
    if (!data.manualTime && count >= totalMax) {
        alert(`⛔ ¡IMPOSIBLE! El día está totalmente lleno (${count} pacientes).`);
        return;
    }

    if (!data.manualTime && count >= limitRegular) {
        if(!window.confirm(`⚠️ El cupo regular (${limitRegular}) está lleno. ¿Deseas usar un CUPO EXTRA?`)) return;
        finalMotivo = `🔴 EXTRA: ${data.motivo}`;
        isExtra = true;
    }

    // 2. Definición de Hora
    let finalTime = '';
    if (data.manualTime && data.manualTime !== "") {
        const [h, m] = data.manualTime.split(':');
        const hour = parseInt(h);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        finalTime = `${hour12 < 10 ? '0'+hour12 : hour12}:${m} ${ampm}`;
    } else {
        finalTime = findFirstAvailableSlot(data.date, appointments);
    }

    const price = data.tipo === 'Examen' ? 0 : (config?.consultaPriceRef || 0);
    
    const newApp = {
        nombre: data.nombre,
        cedula: data.cedula,
        telefono: data.telefono,
        email: data.email || 'No indicado',
        date: data.date,
        assignedTime: finalTime,
        motivo: finalMotivo,
        tipo: data.tipo,
        paymentMethod: 'Por Definir',
        status: 'Pendiente', 
        priceInRef: price,
        paymentRate: config?.dolarRate || 0,
        historyNumber: `HC-${data.cedula.slice(-4)}`,
        createdAt: new Date().toISOString(),
        isExtra: isExtra 
    };

    try {
        await addDoc(collection(db, "appointments"), newApp);
        logAction('MANUAL_BOOK', `Cita manual ${isExtra ? '(EXTRA)' : ''}: ${data.cedula} @ ${finalTime}`);
        alert(`✅ Paciente agregado correctamente a las ${finalTime}.`);
        setShowManualBooking(false);
    } catch (error) {
        alert("Error al guardar: " + error.message);
    }
  };

  const saveClinicalNote = (cedula, text, link) => {
    const newNote = { date: new Date().toISOString(), text, link, doctor: user.label };
    const prevNotes = clinicalRecords?.[cedula] || [];
    const newRecords = { ...(clinicalRecords || {}), [cedula]: [...prevNotes, newNote] };
    setClinicalRecords(newRecords);
    setDoc(doc(db, "system", "clinicalRecords"), newRecords).catch(console.error);
    logAction('CLINICAL', `Nota agregada a ${cedula}`);
  };

  const printDiv = (id) => {
    const content = document.getElementById(id).innerHTML;
    const win = window.open('', '', 'height=700,width=900');
    win.document.write(`<html><head><script src="https://cdn.tailwindcss.com"></script></head><body class="p-8 bg-white">${content}</body></html>`);
    win.document.close(); setTimeout(() => win.print(), 1000);
  };

  const handleConfigChange = (field, value) => {
    const newConfig = { ...config, [field]: value, rateUpdatedAt: field === 'dolarRate' ? getTodayFormatted() : config.rateUpdatedAt };
    setConfig(newConfig);
    setDoc(doc(db, "system", "config"), newConfig).catch(console.error);
    if(field === 'dolarRate') logAction('RATE', `Tasa Manual: ${value}`);
  };

  const fetchBCVRate = async (silent = false) => {
    setLoadingRate(true);
    const currency = config?.currencySource || 'USD';
    let newRate = 0;
    let sourceUsed = '';
    const PROXY_URL = "https://api.allorigins.win/raw?url=";

    try {
        try {
            const targetUrl = `https://pydolarvenezuela-api.vercel.app/api/v1/dollar?page=bcv&monitor=bcv&t=${new Date().getTime()}`;
            const response = await fetch(`${PROXY_URL}${encodeURIComponent(targetUrl)}`);
            if (response.ok) {
                const data = await response.json();
                if (data?.monitors) {
                    if (currency === 'EUR' && data.monitors.eur) newRate = parseFloat(data.monitors.eur.price);
                    else if (data.monitors.usd) newRate = parseFloat(data.monitors.usd.price);
                    sourceUsed = 'PyDolar (Proxy)';
                }
            }
        } catch (e) { console.warn("Fallo Proxy, intentando directo..."); }

        if (newRate === 0) {
            try {
                const endpoint = currency === 'EUR' ? 'https://ve.dolarapi.com/v1/euros/oficial' : 'https://ve.dolarapi.com/v1/dolares/oficial';
                const response = await fetch(endpoint);
                if (response.ok) {
                    const data = await response.json();
                    if (data?.promedio) {
                        newRate = parseFloat(data.promedio);
                        sourceUsed = 'DolarApi';
                    }
                }
            } catch (e) { console.warn("Fallo DolarApi..."); }
        }

        if (newRate > 0) {
            const formattedRate = newRate.toFixed(2);
            if (parseFloat(formattedRate) !== parseFloat(config?.dolarRate)) {
                const newConfig = { ...config, dolarRate: formattedRate, rateUpdatedAt: getTodayFormatted() };
                setConfig(newConfig);
                await setDoc(doc(db, "system", "config"), newConfig);
                const msg = `✅ Tasa actualizada a Bs ${formattedRate} (${sourceUsed})`;
                logAction('AUTO_RATE', msg);
                if (!silent) alert(msg);
            } else {
                if (!silent) alert(`👌 El precio BCV sigue igual (Bs ${formattedRate}). Verificado con ${sourceUsed}.`);
            }
        } else { throw new Error("Todas las APIs fallaron"); }
    } catch (error) {
        console.error("Error crítico tasas:", error);
        if (!silent) alert("⚠️ Las APIs de tasas están saturadas. Ingresa manual.");
    } finally { setLoadingRate(false); }
  };
  
  const handleAuthUpdate = (role, newPass) => {
     if(!newPass || !window.confirm(`¿Cambiar clave de ${role}?`)) return;
     const newAuth = { ...auth, [role]: newPass };
     setAuth(newAuth);
     setDoc(doc(db, "system", "auth"), newAuth);
     alert("Clave actualizada.");
  };

  const addFaq = () => {
     const newFaqs = [...faqs, { id: Date.now(), q: "Nueva Pregunta", a: "Respuesta", keywords: [] }];
     setFaqs(newFaqs); setDoc(doc(db, "system", "faqs"), { list: newFaqs });
  };
  const removeFaq = (id) => {
     const newFaqs = faqs.filter(f => f.id !== id);
     setFaqs(newFaqs); setDoc(doc(db, "system", "faqs"), { list: newFaqs });
  };
  const handleFaqChange = (id, field, value) => {
    const newFaqs = faqs.map(f => f.id === id ? { ...f, [field]: value } : f);
    setFaqs(newFaqs);
    setDoc(doc(db, "system", "faqs"), { list: newFaqs });
  };

  const updateStatus = async (id, newStatus, price = null) => {
    const app = appointments.find(a => a.id === id);
    const updates = { status: newStatus };
    if (price !== null) updates.priceInRef = price; 
    if (newStatus === 'Aprobada' && (!app.status || app.status !== 'Cortesía')) {
        updates.paymentRate = config?.dolarRate; 
    }
    try { await updateDoc(doc(db, "appointments", id), updates); logAction('STATUS', `Cita ${id} -> ${newStatus}`); } catch(e) { alert("Error: " + e.message); }
  };

  const hardDelete = async (id) => {
    if(!window.confirm("⚠️ ¿ELIMINAR DEFINITIVAMENTE?")) return;
    try { await deleteDoc(doc(db, "appointments", id)); logAction('DELETE', `ID ${id}`); } catch(e) { alert("Error"); }
  };

  const clearWeekData = async (weekLabel, appsInWeek) => {
      if(!window.confirm(`⚠️ PELIGRO ⚠️\n\n¿Seguro de BORRAR TODO el historial de ${weekLabel}?`)) return;
      const batch = writeBatch(db);
      appsInWeek.forEach(app => { const ref = doc(db, "appointments", app.id); batch.delete(ref); });
      try { await batch.commit(); logAction('DELETE_WEEK', `Borrado masivo ${weekLabel}`); alert(`Semana eliminada.`); } catch (error) { alert("Error: " + error.message); }
  };

  const clearLogs = async () => {
    if(!window.confirm("¿Borrar historial de la NUBE?")) return;
    try { logs.forEach(log => { deleteDoc(doc(db, "logs", log.id)); }); alert("Historial eliminado."); } catch(e) { alert("Error al borrar logs"); }
  };

  const toggleBooking = () => {
    if(!window.confirm(`¿${config?.isBookingEnabled ? 'CERRAR' : 'ABRIR'} la agenda?`)) return;
    const newState = !config?.isBookingEnabled;
    handleConfigChange('isBookingEnabled', newState);
    if (!newState) addDoc(collection(db, "appointments"), { nombre: "⛔ AGENDA CERRADA", cedula: "SISTEMA", date: new Date().toISOString().split('T')[0], assignedTime: "--:--", status: 'Bloqueo', priceInRef: 0, paymentRate: 0, tipo: 'System', motivo: 'Cierre manual', historyNumber: 'LOG-SYS', createdAt: new Date().toISOString() });
  };
  
  const toggleMaintenance = () => {
    if(!window.confirm("⚠️ ¿CAMBIAR MODO MANTENIMIENTO?")) return;
    handleConfigChange('isMaintenanceMode', !config?.isMaintenanceMode);
  };

  const rescheduleApp = async (app) => {
     const newDate = prompt("Nueva Fecha (DD-MM-AAAA):", app.date);
     if(!newDate) return;
     const isRemotePaid = (app.paymentMethod === 'Pago Móvil' || app.paymentMethod === 'Transferencia' || app.paymentMethod === 'Zelle');
     const isApproved = app.status === 'Aprobada';
     const newPrice = (isApproved && isRemotePaid) ? 0 : parseFloat(app.priceInRef);
     const newMotivo = (isApproved && isRemotePaid) ? `${app.motivo} (Reprogramada, Pagada)` : app.motivo;

     if(isApproved && isRemotePaid) { await updateDoc(doc(db, "appointments", app.id), { motivo: `${app.motivo} (ORIGEN REPROGRAMADO)` }); 
     } else { await updateDoc(doc(db, "appointments", app.id), { status: 'Cancelada', motivo: `${app.motivo} (REPROGRAMADA)` }); }
     
     const finalTime = findFirstAvailableSlot(newDate, appointments);
     
     const newApp = { ...app, date: newDate, status: 'Pendiente', priceInRef: newPrice, motivo: newMotivo, id: null, assignedTime: finalTime, createdAt: new Date().toISOString() };
     delete newApp.id; 
     await addDoc(collection(db, "appointments"), newApp);
     alert(`Cita reprogramada para el ${newDate} a las ${finalTime}`);
  };

  const getFilteredAppointments = () => {
    let apps = appointments;
    if (filterWeek !== 'all') apps = apps.filter(a => `Semana ${getWeekNumber(a.date)}` === filterWeek);
    return apps.filter(a => searchTerm === '' || a.cedula.includes(searchTerm) || a.historyNumber.toLowerCase().includes(searchTerm.toLowerCase())).sort((a,b) => {
          if(a.date !== b.date) return new Date(a.date) - new Date(b.date);
          const timeA = parseTime(a.assignedTime); const timeB = parseTime(b.assignedTime); return timeA - timeB; });
  };
  
  const groupApps = (apps) => {
     const grouped = {};
     apps.forEach(a => { const wk = `Semana ${getWeekNumber(a.date)}`; const day = `${getDayName(a.date)} ${formatDate(a.date)}`; if(!grouped[wk]) grouped[wk] = {}; if(!grouped[wk][day]) grouped[wk][day] = []; grouped[wk][day].push(a); });
     return grouped;
  };

  const filteredAppsForCount = getFilteredAppointments();
  const groupedApps = groupApps(filteredAppsForCount);
  const financeApps = appointments.filter(a => (a.status === 'Aprobada' || a.status === 'Bloqueo' || a.status === 'Cortesía') && (searchFinance === '' || a.cedula.includes(searchFinance) || a.historyNumber.toLowerCase().includes(searchFinance.toLowerCase())));
  const groupedFinance = financeApps.filter(a => filterWeek === 'all' || `Semana ${getWeekNumber(a.date)}` === filterWeek).reduce((acc, curr) => { const wk = `Semana ${getWeekNumber(curr.date)}`; if(!acc[wk]) acc[wk] = []; acc[wk].push(curr); return acc; }, {});

  const downloadCSV = () => {
     const rows = [["Fecha", "HC", "Paciente", "Cedula", "Metodo", "Total($)", "Tasa", "P.Admin($)", "Neto($)", "Clinica($)", "Clinica(Bs)", "Doc($)", "Doc(Bs)"], ...appointments.filter(a => a.status === 'Aprobada').map(a => {
            const rate = a.paymentRate || config?.dolarRate; const total = parseFloat(a.priceInRef); const admin = a.tipo === 'Examen' ? 0 : (config?.adminFee || 0); const net = total - admin; const cliU = net * ((config?.clinicShare || 30)/100); const cliB = cliU * rate; const docU = net * ((100-(config?.clinicShare || 30))/100); const docB = docU * rate;
            return [formatDate(a.date), a.historyNumber, a.nombre, a.cedula, a.paymentMethod, total, rate, admin, net.toFixed(2), cliU.toFixed(2), cliB.toFixed(2), docU.toFixed(2), docB.toFixed(2)]; })];
     const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
     const link = document.createElement("a"); link.setAttribute("href", encodeURI(csvContent)); link.setAttribute("download", "reporte_financiero_completo.csv"); document.body.appendChild(link); link.click();
  };

  const generatePDF = (type, app) => {
    const win = window.open('', '', 'height=700,width=900');
    let title = type === 'pago' ? 'RECIBO DE PAGO' : 'RECORDATORIO DE CITA';
    if(type === 'reminder') title = 'RECORDATORIO DE PAGO';
    let color = type === 'pago' ? 'border-green-600' : 'border-blue-600'; if(type === 'reminder') color = 'border-yellow-500';
    const rate = app.paymentRate || config?.dolarRate; const amount = parseFloat(app.priceInRef); const totalBs = (amount * rate).toFixed(2);
    let extraInfo = '';
    if (type === 'pago') { extraInfo = `<div class="mt-4 bg-gray-50 p-4 rounded border border-gray-200 text-sm"><h3 class="font-bold text-gray-700 border-b pb-1 mb-2">DETALLE FINANCIERO</h3><div class="flex justify-between mb-1"><span>Monto Ref:</span><span>$${amount}</span></div><div class="flex justify-between mb-1"><span>Tasa:</span><span>Bs ${rate}</span></div><div class="flex justify-between font-bold text-blue-900 border-t pt-2 mt-1"><span>TOTAL PAGADO:</span><span>Bs ${totalBs}</span></div><p class="text-xs text-gray-500 mt-2 text-right">Método: ${app.paymentMethod}</p></div>`; } 
    else if (type === 'reminder') { const isRemote = ['Pago Móvil', 'Transferencia', 'Zelle'].includes(app.paymentMethod); const msg = isRemote ? "Para confirmar su asistencia, es necesario que realice el pago y envíe el comprobante." : "Su hora está apartada. Recuerde que el proceso se completa pagando en el consultorio."; extraInfo = `<div class="mt-4 p-4 bg-yellow-50 rounded border border-yellow-200 text-sm text-yellow-900"><h3 class="font-bold mb-2">PENDIENTE POR CONFIRMAR</h3><p>${msg}</p><div class="font-bold mt-2">Monto a Pagar: $${amount} (Bs ${totalBs})</div></div>`; } 
    else { extraInfo = `<div class="mt-4 p-4 bg-blue-50 rounded border border-blue-100 text-sm text-blue-800"><h3 class="font-bold mb-2">INSTRUCCIONES:</h3><ul class="list-disc pl-5"><li>Llegar 15 min antes.</li><li>Traer exámenes previos.</li></ul></div>`; }
    win.document.write(`<html><head><script src="https://cdn.tailwindcss.com"></script></head><body class="p-8 font-sans"><div class="border-4 ${color} p-6 rounded-xl max-w-2xl mx-auto shadow-xl"><div class="flex justify-between items-start border-b-2 pb-4 mb-6"><div><h1 class="text-3xl font-bold text-gray-800 tracking-tight">${title}</h1><p class="text-lg font-bold text-gray-600">${BRANDING.doctorName}</p><p class="text-xs text-gray-400 uppercase tracking-widest">${BRANDING.specialty}</p></div><div class="text-right"><div class="bg-gray-100 p-2 rounded-lg text-center"><p class="font-mono text-2xl font-bold text-gray-700">#${app.historyNumber}</p><p class="text-[10px] uppercase tracking-widest text-gray-400">Control Interno</p></div></div></div><div class="grid grid-cols-2 gap-8 text-sm mb-6"><div><p class="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1">PACIENTE</p><p class="font-bold text-lg">${app.nombre}</p><p class="text-gray-600">C.I: ${app.cedula}</p><p>Tel: ${app.telefono}</p><p class="text-blue-500 font-bold text-xs">${app.email || ''}</p></div><div class="text-right"><p class="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1">FECHA Y HORA</p><p class="font-bold text-lg">${formatDate(app.date)}</p><p class="text-blue-600 font-bold text-2xl">${app.assignedTime}</p></div></div><div class="mb-6"><p class="text-xs text-gray-400 uppercase tracking-wider font-bold border-b mb-1">MOTIVO</p><p class="text-base font-medium">${app.motivo}</p></div>${extraInfo}<div class="mt-8 pt-4 border-t-2 border-dashed border-gray-300 text-center"><p class="text-xs font-bold uppercase tracking-widest">Firma Autorizada</p></div></div></body></html>`);
    win.document.close(); setTimeout(() => win.print(), 1000);
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* HEADER DEL PANEL */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 bg-white p-4 rounded shadow border-l-4 border-blue-900 gap-4">
        <div>
          <h2 className="text-xl font-bold text-blue-900 flex items-center gap-2"><Lock size={20}/> Panel {user.label}</h2>
          <p className="text-xs text-gray-500">Sesión Segura</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-center md:justify-end items-center">
            {/* BOTÓN NUEVO: Agendamiento Manual */}
           {(can('calendar') || can('booking')) && (
               <button onClick={() => setShowManualBooking(true)} className="bg-blue-600 text-white px-4 py-2 rounded font-bold shadow hover:bg-blue-700 flex items-center gap-2 mr-2 animate-pulse">
                   <UserPlus size={16} /> Nuevo Paciente
               </button>
           )}
           {(can('calendar') || can('booking')) && <button onClick={() => setTab('citas')} className={`px-3 py-1 rounded font-bold ${tab === 'citas' ? 'bg-blue-800 text-white' : 'bg-gray-200'}`}>Citas</button>}
           {can('finance') && <button onClick={() => setTab('finanzas')} className={`px-3 py-1 rounded font-bold ${tab === 'finanzas' ? 'bg-green-700 text-white' : 'bg-gray-200'}`}>Finanzas</button>}
           {can('all') && <button onClick={() => setTab('logs')} className={`px-3 py-1 rounded font-bold ${tab === 'logs' ? 'bg-gray-800 text-white' : 'bg-gray-200'}`}>Auditoría</button>}
           {can('all') && <button onClick={() => setTab('config')} className={`px-3 py-1 rounded font-bold ${tab === 'config' ? 'bg-orange-600 text-white' : 'bg-gray-200'}`}>Config</button>}
           <button onClick={() => { logAction('LOGOUT', 'Salida'); setAdminAuth(false); }} className="text-red-500 underline ml-4 font-bold">Salir</button>
        </div>
      </div>

      {tab === 'citas' && (can('calendar') || can('booking')) && (
        <div className="bg-white rounded shadow p-4 w-full">
          <div className="flex flex-col md:flex-row justify-between mb-4 items-center gap-4">
             <div className="flex flex-col text-center md:text-left">
                <h3 className="font-bold text-lg">Agenda Detallada</h3>
                <p className="text-xs text-blue-600 font-bold bg-blue-50 px-2 py-1 rounded border border-blue-200 w-fit mx-auto md:mx-0 mt-1">
                   Pacientes en lista: {filteredAppsForCount.length}
                </p>
             </div>
             <div className="flex flex-col md:flex-row gap-2 items-center w-full md:w-auto">
                <div className="relative w-full md:w-auto">
                    <Search size={14} className="absolute left-2 top-2 text-gray-400"/>
                    <input placeholder="Buscar Paciente..." className="pl-7 p-2 border rounded text-sm w-full" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <select className="bg-gray-100 p-2 rounded text-xs flex-1 md:flex-none" onChange={e => setFilterWeek(e.target.value)}><option value="all">Todas Semanas</option>{Array.from(new Set(appointments.map(a => `Semana ${getWeekNumber(a.date)}`))).sort().map(w => <option key={w} value={w}>{w}</option>)}</select>
                    <button onClick={() => printDiv('print-citas')} className="bg-gray-800 text-white px-3 py-2 rounded text-xs"><Printer size={16}/></button>
                </div>
             </div>
          </div>
          
          <div id="print-citas" className="overflow-x-auto w-full">
             {Object.keys(groupedApps).sort().map(wk => (
                 <div key={wk} className="mb-6 border rounded-lg overflow-hidden min-w-[600px] md:min-w-0">
                     <div className="bg-blue-100 p-2 font-bold text-blue-900 flex justify-between items-center border-b border-blue-200">
                        <span>{wk}</span>
                        {can('all') && (
                            <button onClick={() => {
                                const appsInWeek = Object.values(groupedApps[wk]).flat();
                                clearWeekData(wk, appsInWeek);
                            }} className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 flex items-center gap-1">
                                <Trash2 size={12}/> Borrar Semana
                            </button>
                        )}
                     </div>
                     {Object.keys(groupedApps[wk]).sort().map(day => (
                         <div key={day} className="border-b last:border-0">
                            <div className="bg-gray-50 p-1 px-3 font-bold text-xs text-gray-600 border-b border-gray-100">{day}</div>
                            <table className="w-full text-sm text-left">
                                <thead className="bg-white text-xs text-gray-400"><tr><th className="p-1 pl-3">#</th><th>HC</th><th>Paciente</th><th>Tlf</th><th>Hora</th><th>Motivo</th><th>Pago</th><th>Status</th><th className="no-print text-center">Acción</th></tr></thead>
                                <tbody>
                                    {groupedApps[wk][day].map((app, index) => (
                                        <tr key={app.id} className={`border-b hover:bg-blue-50 ${app.status === 'Bloqueo' ? 'bg-gray-200' : app.tipo === 'Examen' ? 'bg-purple-50' : app.status === 'Cortesía' ? 'bg-orange-50' : ''}`}>
                                            <td className="p-2 pl-3 font-bold text-gray-400">{index + 1}</td>
                                            <td className="p-2 pl-3 font-mono text-xs text-blue-800 font-bold">{app.historyNumber}</td>
                                            <td className="p-2">
                                                <strong>{app.nombre}</strong>
                                                {(app.isExtra || (app.motivo && app.motivo.includes('EXTRA'))) && (
                                                    <span className="ml-2 bg-red-100 text-red-800 text-[10px] font-bold px-1 rounded border border-red-200 animate-pulse">🔴 EXTRA</span>
                                                )}
                                                <div className="text-[10px] text-gray-500 font-normal">{app.cedula} - {app.sexo}</div>
                                                <div className="text-[10px] text-blue-500 font-bold">{app.email}</div>
                                            </td>
                                            <td className="p-2 text-xs">{app.telefono}</td>
                                            <td className="p-2 font-bold">{app.assignedTime}</td>
                                            <td className="p-2 text-xs cursor-pointer group" onClick={(e) => { 
                                                const el = e.currentTarget.querySelector('div');
                                                if (el) el.classList.toggle('truncate');
                                            }} title="Clic para ver completo">
                                                <div className="truncate max-w-[150px]">{app.motivo}</div>
                                                <span className="text-[8px] text-blue-500 block">(Ver más)</span>
                                            </td>
                                            <td className="p-2 text-xs">{app.paymentMethod}</td>
                                            <td className="p-2"><span className={`px-2 py-0.5 rounded text-[10px] border ${app.status === 'Aprobada' ? 'bg-green-100 border-green-300 text-green-800' : app.status === 'Cortesía' ? 'bg-orange-200 border-orange-300 text-orange-800' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>{app.status}</span></td>
                                            <td className="p-2 no-print flex gap-1 justify-center">
                                                {app.status === 'Pendiente' && <>{(can('finance') || can('booking')) && <button onClick={() => updateStatus(app.id, 'Aprobada')} className="bg-green-500 text-white p-1 rounded" title="Aprobar Pago"><Check size={12}/></button>}<button onClick={() => generatePDF('reminder', app)} className="bg-yellow-500 text-white p-1 rounded" title="Recordatorio Pago"><DollarSign size={12}/></button><button onClick={() => rescheduleApp(app)} className="bg-blue-400 text-white p-1 rounded" title="Reprogramar"><RefreshCw size={12}/></button><button onClick={() => updateStatus(app.id, 'Cancelada')} className="bg-gray-500 text-white p-1 rounded"><Ban size={12}/></button><button onClick={() => updateStatus(app.id, 'Cortesía', 0)} className="bg-orange-500 text-white p-1 rounded" title="Cortesía/Extra (No cobra)"><Gift size={12}/></button></>}
                                                {app.status === 'Aprobada' && <><button onClick={() => generatePDF('cita', app)} className="bg-blue-500 text-white p-1 rounded"><Printer size={12}/></button><button onClick={() => generatePDF('pago', app)} className="bg-green-600 text-white p-1 rounded"><DollarSign size={12}/></button><button onClick={() => rescheduleApp(app)} className="bg-blue-400 text-white p-1 rounded" title="Reprogramar"><RefreshCw size={12}/></button></>}
                                                {can('all') && app.status !== 'Bloqueo' && <button onClick={() => setSelectedPatient(app)} className="bg-purple-600 text-white p-1 rounded"><FileText size={12}/></button>}
                                                {can('all') && <button onClick={() => hardDelete(app.id)} className="bg-red-600 text-white p-1 rounded"><Trash2 size={12}/></button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                         </div>
                     ))}
                 </div>
             ))}
          </div>
        </div>
      )}

      {tab === 'finanzas' && can('finance') && (
        <div className="bg-white rounded shadow p-4 w-full">
           <div className="flex flex-col md:flex-row justify-between mb-4 items-center gap-4">
              <h3 className="font-bold text-lg text-green-700">Finanzas Detalladas</h3>
              <div className="flex flex-col md:flex-row gap-2 items-center w-full md:w-auto">
                <div className="relative w-full md:w-auto">
                    <Search size={14} className="absolute left-2 top-2 text-gray-400"/>
                    <input placeholder="Buscar HC..." className="pl-7 p-2 border rounded text-sm w-full" value={searchFinance} onChange={e => setSearchFinance(e.target.value)}/>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <select className="bg-transparent text-xs outline-none bg-gray-100 p-2 rounded flex-1 md:flex-none" onChange={e => setFilterWeek(e.target.value)}><option value="all">Todas Semanas</option>{Array.from(new Set(appointments.map(a => `Semana ${getWeekNumber(a.date)}`))).sort().map(w => <option key={w} value={w}>{w}</option>)}</select>
                    <button onClick={downloadCSV} className="bg-green-600 text-white px-3 py-2 rounded text-sm flex items-center gap-1"><Download size={14}/> Excel</button>
                    <button onClick={() => printDiv('print-finanzas')} className="bg-gray-800 text-white px-3 py-2 rounded text-sm"><Printer size={16}/></button>
                </div>
              </div>
           </div>
           
           <div id="print-finanzas" className="overflow-x-auto w-full">
             {Object.keys(groupedFinance).sort().map(wk => {
                const list = groupedFinance[wk];
                let T_USD=0, T_FEE=0, T_NETO=0, T_CLI_USD=0, T_CLI_BS=0, T_DOC_USD=0, T_DOC_BS=0;
                
                const statsByMethod = {};

                list.forEach(r => {
                   if (r.status === 'Cortesía' || r.status === 'Bloqueo') return;
                   
                   const rate = r.paymentRate || config?.dolarRate;
                   const total = parseFloat(r.priceInRef);
                   const isExam = r.tipo === 'Examen';
                   const admin = isExam ? 0 : (config?.adminFee || 0);
                   const net = total - admin;

                   T_USD += total;
                   T_FEE += admin;
                   T_NETO += net;
                   T_CLI_USD += (net * ((config?.clinicShare || 30)/100));
                   T_DOC_USD += (net * ((100-(config?.clinicShare || 30))/100));
                   
                   const method = r.paymentMethod || 'Desconocido';
                   if (!statsByMethod[method]) statsByMethod[method] = { count: 0, usd: 0, bs: 0 };
                   
                   statsByMethod[method].count++;
                   statsByMethod[method].usd += total;
                   
                   const isHardCurrency = ['Divisas', 'Efectivo ($)', 'Zelle'].some(m => method.includes(m));
                   if (isHardCurrency) {
                       statsByMethod[method].bs += total; 
                   } else {
                       statsByMethod[method].bs += (total * rate);
                   }
                });
                
                T_CLI_BS = T_CLI_USD * (config?.dolarRate || 0); 
                T_DOC_BS = T_DOC_USD * (config?.dolarRate || 0);

                return (
                  <div key={wk} className="mb-10 border rounded-lg overflow-hidden shadow-sm page-break-inside-avoid min-w-[700px] md:min-w-0">
                     <div className="bg-gray-800 text-white p-2 font-bold text-center flex justify-between items-center px-4">
                        <span>{wk}</span>
                        {can('all') && (
                            <button onClick={() => {
                                const appsInWeek = list; 
                                clearWeekData(wk, appsInWeek);
                            }} className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs flex items-center gap-2">
                                <Trash2 size={14} /> Limpiar Historial Semana
                            </button>
                        )}
                     </div>

                     <table className="w-full text-xs text-center border-collapse mb-0">
                        <thead className="bg-gray-50 border-b font-bold text-gray-600">
                           <tr>
                              <th className="p-2">#</th>
                              <th>Fecha</th>
                              <th>HC</th>
                              <th>Paciente</th>
                              <th>Método</th>
                              <th>Total($)</th>
                              <th className="text-red-600">P.Admin(${config?.adminFee})</th>
                              <th>Neto($)</th>
                              <th className="text-blue-600">Clin({config?.clinicShare}%)</th>
                              <th className="text-green-600">Doc({100-(config?.clinicShare || 30)}%)</th>
                              <th className="text-green-600 font-bold bg-green-50">Doc(Bs)</th>
                              <th>Tasa</th>
                           </tr>
                        </thead>
                        <tbody>{list.map((r, i) => {
                           if(r.status === 'Bloqueo') return <tr key={r.id} className="bg-gray-200"><td colSpan="12" className="p-1 font-bold text-gray-500">AGENDA CERRADA</td></tr>;
                           if(r.status === 'Cortesía') return <tr key={r.id} className="bg-orange-50 text-orange-800"><td>{i+1}</td><td className="p-1">{formatDate(r.date)}</td><td>{r.historyNumber}</td><td className="text-left">{r.nombre} (CORTESÍA)</td><td>-</td><td>$0</td><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td></tr>;
                           
                           const rate = r.paymentRate || config?.dolarRate;
                           const total = parseFloat(r.priceInRef);
                           const isExam = r.tipo === 'Examen';
                           const admin = isExam ? 0 : (config?.adminFee || 0);
                           const net = total - admin;
                           const cli = net * ((config?.clinicShare || 30)/100);
                           const doc = net * ((100-(config?.clinicShare || 30))/100);
                           const docBs = doc * rate;

                           return (
                             <tr key={r.id} className="border-b hover:bg-gray-50">
                               <td className="font-bold text-gray-400">{i+1}</td>
                               <td className="p-1">{formatDate(r.date)}</td>
                               <td className="font-mono text-gray-500">{r.historyNumber}</td>
                               <td className="text-left px-2">{r.nombre}</td>
                               <td>{r.paymentMethod}</td>
                               <td className="font-bold">${total}</td>
                               <td className="text-red-600">-${admin}</td>
                               <td className="font-bold">${net.toFixed(2)}</td>
                               <td className="text-blue-600">${cli.toFixed(2)}</td>
                               <td className="text-green-600">${doc.toFixed(2)}</td>
                               <td className="text-green-800 font-bold bg-green-50">Bs {docBs.toFixed(2)}</td>
                               <td className="text-gray-400 text-[10px]">Bs {rate}</td>
                             </tr>
                           )
                        })}</tbody>
                     </table>

                     <div className="bg-gray-50 border-t border-gray-300 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm justify-items-end md:justify-items-center">
                            <div className="text-right w-full md:w-auto border-b md:border-b-0 pb-2 md:pb-0">
                                <p className="text-xs font-bold text-gray-400 uppercase">Total Bruto</p>
                                <p className="text-lg font-bold">${T_USD.toFixed(2)}</p>
                            </div>
                            <div className="text-right text-red-600 w-full md:w-auto border-b md:border-b-0 pb-2 md:pb-0">
                                <p className="text-xs font-bold uppercase">P.Admin</p>
                                <p className="text-lg font-bold">-${T_FEE.toFixed(2)}</p>
                            </div>
                            <div className="text-right w-full md:w-auto border-b md:border-b-0 pb-2 md:pb-0">
                                <p className="text-xs font-bold text-blue-800 uppercase">Clínica ({config?.clinicShare}%)</p>
                                <p className="text-lg font-bold text-blue-600">${T_CLI_USD.toFixed(2)}</p>
                                <p className="text-xs text-gray-500">Aprox: Bs {T_CLI_BS.toFixed(2)}</p>
                            </div>
                            <div className="text-right w-full md:w-auto bg-green-50 p-2 rounded">
                                <p className="text-xs font-bold text-green-800 uppercase">Doctor ({100-(config?.clinicShare || 30)}%)</p>
                                <p className="text-xl font-bold text-green-600">${T_DOC_USD.toFixed(2)}</p>
                                <p className="text-sm font-bold text-green-800">Bs {T_DOC_BS.toFixed(2)}</p>
                            </div>
                        </div>

                        {/* DESGLOSE POR MÉTODO DE PAGO */}
                        <div className="mt-4 border-t pt-2 no-print-break">
                           <button 
                             onClick={() => toggleSummary(wk)}
                             className="w-full text-left text-xs font-bold text-gray-500 hover:text-blue-600 flex items-center justify-between p-2 bg-gray-100 rounded"
                           >
                              <span>🧾 Ver Desglose por Método de Pago</span>
                              {expandedSummary === wk ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                           </button>
                           
                           {expandedSummary === wk && (
                             <div className="mt-2 bg-white border rounded p-2 animate-fade-in">
                                <table className="w-full text-xs text-left">
                                   <thead className="bg-gray-100 font-bold">
                                      <tr><th>Método</th><th className="text-center">Cant.</th><th className="text-right">Total ($)</th><th className="text-right">Total (Bs)</th></tr>
                                   </thead>
                                   <tbody>
                                      {Object.entries(statsByMethod).map(([method, data]) => {
                                         const isHardCurrency = ['Divisas', 'Efectivo ($)', 'Zelle'].some(m => method.includes(m));
                                         const finalBs = isHardCurrency ? (data.usd * (config?.dolarRate || 0)) : data.bs;

                                         return (
                                           <tr key={method} className="border-b last:border-0">
                                              <td className="p-1 font-bold text-gray-600">{method}</td>
                                              <td className="text-center">{data.count}</td>
                                              <td className="text-right font-mono">${data.usd.toFixed(2)}</td>
                                              <td className="text-right font-mono text-gray-500">Bs {finalBs.toFixed(2)}</td>
                                           </tr>
                                         );
                                      })}
                                   </tbody>
                                </table>
                             </div>
                           )}
                        </div>

                        <div className="mt-2 text-center text-[10px] text-gray-400">
                            * Los montos en Bolívares del resumen general se calculan a la tasa actual ({config?.dolarRate || 0}). Las citas individuales muestran su tasa histórica.
                        </div>
                     </div>
                  </div>
                )
             })}
           </div>
        </div>
      )}

      {tab === 'logs' && can('all') && (
        <div className="bg-white rounded shadow p-4 h-96 overflow-y-auto">
            <div className="flex justify-between mb-4">
                <h3 className="font-bold text-lg flex items-center gap-2"><Shield/> Logs</h3>
                <button onClick={clearLogs} className="text-xs bg-red-100 text-red-800 px-2 rounded font-bold border border-red-300 hover:bg-red-200">LIMPIAR HISTORIAL (DATABASE)</button>
            </div>
            <table className="w-full text-xs text-left">
                <thead className="bg-gray-100">
                    <tr><th>Fecha</th><th>Usuario</th><th>Acción</th><th>Detalle</th></tr>
                </thead>
                <tbody>
                    {(logs || []).slice().reverse().map((l, i) => (
                        <tr key={i} className="border-b font-mono">
                            <td className="p-1">{new Date(l.date).toLocaleString()}</td>
                            <td className="font-bold text-blue-800">{l.user}</td>
                            <td>{l.action}</td>
                            <td className="text-gray-500">{l.detail}</td>
                        </tr>
                    ))}
                    {(logs || []).length === 0 && <tr><td colSpan="4" className="text-center p-4 text-gray-400">No hay registros aún</td></tr>}
                </tbody>
            </table>
        </div>
      )}

      {tab === 'config' && can('all') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded shadow">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <button onClick={toggleBooking} className={`p-3 rounded-lg text-white font-bold shadow transition flex flex-col items-center ${config?.isBookingEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                    <div className="flex items-center justify-center gap-2 text-xl"><Power size={24}/> {config?.isBookingEnabled ? 'AGENDA ABIERTA' : 'AGENDA CERRADA'}</div>
                    <p className="text-xs font-normal opacity-90 mt-1">Click para cambiar estado</p>
                </button>
                <button onClick={toggleMaintenance} className={`p-3 rounded-lg text-white font-bold shadow transition flex flex-col items-center ${config?.isMaintenanceMode ? 'bg-orange-600' : 'bg-gray-500'}`}>
                    <div className="flex items-center justify-center gap-2 text-xl"><Shield size={24} className="mb-1"/>MANTENIMIENTO {config?.isMaintenanceMode ? 'ON' : 'OFF'}</div>
                    <p className="text-xs font-normal opacity-90 mt-1">Bloquear acceso público</p>
                </button>
              </div>
              <h4 className="font-bold border-b pb-2 mb-2">Límites y Cupos (Control de Agenda)</h4>
              <div className="grid grid-cols-2 gap-2 text-sm mb-4 bg-yellow-50 p-2 border border-yellow-200 rounded">
                  <div><label className="font-bold text-gray-700">Límite Diario Regular</label><input type="number" value={config?.dailyLimit || 10} onChange={e => handleConfigChange('dailyLimit', e.target.value)} className="w-full border p-1 rounded font-bold text-center"/></div>
                  <div><label className="font-bold text-gray-700">Límite Cupos Extra</label><input type="number" value={config?.extraLimit || 2} onChange={e => handleConfigChange('extraLimit', e.target.value)} className="w-full border p-1 rounded font-bold text-center text-red-600"/></div>
                  <p className="col-span-2 text-[10px] text-gray-500">* El público solo podrá agendar hasta el límite regular. Los cupos extra solo se pueden asignar manualmente desde este panel.</p>
              </div>

              {/* --- NUEVA SECCIÓN DE AUTOMATIZACIÓN DE CORREO (ROBOT) --- */}
              <h4 className="font-bold border-b pb-2 mb-2 mt-4 text-blue-800 flex items-center gap-2">
                  <Mail size={16}/> Configuración de Mensajes y Automatización
              </h4>
              <div className="grid grid-cols-1 gap-2 text-sm mb-4 bg-blue-50 p-3 rounded border border-blue-200">
                  
                  {/* Mensaje de Éxito (Frontend) */}
                  <div className="bg-white p-2 rounded border border-blue-100 mb-2">
                      <label className="font-bold text-gray-700 block mb-1">Mensaje de Éxito (Web)</label>
                      <textarea placeholder="Ej: Su cita ha sido pre-agendada..." value={config?.successMessage || ''} onChange={e => handleConfigChange('successMessage', e.target.value)} className="w-full border p-2 rounded h-12 text-xs"/>
                  </div>

                  {/* Credenciales del Robot */}
                  <div className="bg-white p-2 rounded border border-blue-100 mb-2">
                      <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">1. Credenciales del Robot (Quién envía)</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                              <label className="font-bold text-gray-700 text-xs">Gmail del Robot</label>
                              <input placeholder="robot@consultorio.com" value={config?.emailUser || ''} onChange={e => handleConfigChange('emailUser', e.target.value)} className="w-full border p-1 rounded"/>
                          </div>
                          <div>
                              <label className="font-bold text-gray-700 text-xs">Contraseña de Aplicación</label>
                              <div className="flex">
                                  <input type={showPassword['emailPass'] ? "text" : "password"} value={config?.emailPass || ''} onChange={e => handleConfigChange('emailPass', e.target.value)} className="w-full border p-1 rounded-l"/>
                                  <button onClick={() => setShowPassword(p => ({...p, emailPass: !p.emailPass}))} className="bg-gray-200 px-2 rounded-r"><Eye size={12}/></button>
                              </div>
                          </div>
                      </div>
                  </div>

                  {/* Destinatarios y Reglas */}
                  <div className="bg-white p-2 rounded border border-blue-100 mb-2">
                      <p className="text-[10px] text-gray-500 uppercase font-bold mb-1">2. Destinatarios de Reportes</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* ADMIN / DOCTOR */}
                          <div className="border p-2 rounded bg-gray-50">
                              <label className="flex items-center gap-2 cursor-pointer mb-2 border-b pb-1">
                                  <input type="checkbox" checked={config?.enableAdminDailyReport || false} onChange={e => handleConfigChange('enableAdminDailyReport', e.target.checked)} />
                                  <span className="font-bold text-blue-900 text-xs">🔔 Reporte al Doctor</span>
                              </label>
                              <p className="text-[10px] text-gray-500 mb-1">Llega al correo del Robot.</p>
                          </div>

                          {/* SECRETARÍA (NUEVO) */}
                          <div className="border p-2 rounded bg-gray-50">
                              <label className="flex items-center gap-2 cursor-pointer mb-2 border-b pb-1">
                                  <input type="checkbox" checked={config?.enableSecretaryReport || false} onChange={e => handleConfigChange('enableSecretaryReport', e.target.checked)} />
                                  <span className="font-bold text-purple-900 text-xs">👩‍💼 Reporte a Secretaría</span>
                              </label>
                              <input placeholder="correo.secretaria@gmail.com" value={config?.secretaryEmail || ''} onChange={e => handleConfigChange('secretaryEmail', e.target.value)} className="w-full border p-1 rounded text-xs bg-white"/>
                              <p className="text-[10px] text-gray-500 mt-1">Recibe copia de la agenda.</p>
                          </div>
                      </div>
                  </div>

                  {/* Pacientes */}
                  <div className="border p-2 rounded bg-white">
                      <label className="flex items-center gap-2 cursor-pointer mb-1">
                          <input type="checkbox" checked={config?.enablePatientReminder || false} onChange={e => handleConfigChange('enablePatientReminder', e.target.checked)} />
                          <span className="font-bold text-green-900 text-xs">🔔 Recordar a Pacientes (Individual)</span>
                      </label>
                      <p className="text-[10px] text-gray-500">Correo automático 24-30h antes de la cita.</p>
                  </div>
              </div>

              <h4 className="font-bold border-b pb-2 mb-2">Variables Financieras</h4>
              <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                  <div><label>Tasa BCV/Oficial</label><input type="number" value={config?.dolarRate || 0} onChange={e => handleConfigChange('dolarRate', e.target.value)} className="w-full border p-1 rounded"/></div>
                  <div><label>Precio Consulta ($)</label><input type="number" value={config?.consultaPriceRef || 0} onChange={e => handleConfigChange('consultaPriceRef', e.target.value)} className="w-full border p-1 rounded"/></div>
                  <div><label>P. Administrativo ($)</label><input type="number" value={config?.adminFee || 0} onChange={e => handleConfigChange('adminFee', e.target.value)} className="w-full border p-1 rounded"/></div>
                  <div><label>Límite Exámenes</label><input type="number" value={config?.examLimit || 2} onChange={e => handleConfigChange('examLimit', e.target.value)} className="w-full border p-1 rounded"/></div>
              </div>
              
              {/* AUTO-ACTUALIZACIÓN */}
              <div className="bg-blue-50 p-3 rounded border border-blue-200 mb-6">
                  <h5 className="font-bold text-blue-900 text-xs flex items-center gap-1 mb-2"><Globe size={14}/> Automatización de Tasa (Anti-Bloqueo)</h5>
                  <div className="flex items-center gap-4 text-xs mb-2">
                      <label className="flex items-center gap-1 cursor-pointer">
                          <input type="checkbox" checked={config?.autoUpdateBCV || false} onChange={e => handleConfigChange('autoUpdateBCV', e.target.checked)} />
                          <span>🔄 Actualización Automática</span>
                      </label>
                      <select className="border p-1 rounded" value={config?.currencySource || 'USD'} onChange={e => handleConfigChange('currencySource', e.target.value)}>
                          <option value="USD">Dólar BCV (USD)</option>
                          <option value="EUR">Euro BCV (EUR)</option>
                      </select>
                  </div>
                  <button 
                      onClick={() => fetchBCVRate(false)} 
                      disabled={loadingRate}
                      className={`w-full text-white font-bold py-2 rounded text-xs flex justify-center items-center gap-2 ${loadingRate ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                      {loadingRate ? <RefreshCw className="animate-spin" size={14}/> : <RefreshCw size={14}/>}
                      {loadingRate ? "Consultando APIs..." : "Actualizar Tasa Ahora (Manual)"}
                  </button>
                  <p className="text-[10px] text-gray-500 mt-1 text-center">Fuente: PyDolar / DolarApi (via Proxy)</p>
              </div>

              <h4 className="font-bold border-b pb-2 mb-2 mt-4">Textos Web</h4>
              <div className="space-y-2 text-sm">
                  <div><label>Aviso Importante</label><textarea value={config?.importantInfo || ''} onChange={e => handleConfigChange('importantInfo', e.target.value)} className="w-full border p-1 rounded h-16"/></div>
                  <div><label>Paso 1</label><input value={config?.step1 || ''} onChange={e => handleConfigChange('step1', e.target.value)} className="w-full border p-1 rounded"/></div>
                  <div><label>Paso 2</label><input value={config?.step2 || ''} onChange={e => handleConfigChange('step2', e.target.value)} className="w-full border p-1 rounded"/></div>
                  <div><label>Paso 3</label><input value={config?.step3 || ''} onChange={e => handleConfigChange('step3', e.target.value)} className="w-full border p-1 rounded"/></div>
                  <div><label>WhatsApp (Sin +)</label><input value={config?.whatsapp || ''} onChange={e => handleConfigChange('whatsapp', e.target.value)} className="w-full border p-1 rounded"/></div>
              </div>
              <h4 className="font-bold border-b pb-2 mb-2 mt-4 text-blue-800">Gestión de Claves</h4>
              <div className="grid grid-cols-3 gap-2 text-xs">
                  {auth && Object.keys(auth).map(k => (
                     <div key={k} className="bg-gray-100 p-2 rounded relative">
                        <p className="font-bold uppercase text-gray-500 mb-1">{k}</p>
                        <div className="flex">
                           <input className="w-full border p-1" type={showPassword[k] ? "text" : "password"} defaultValue={auth[k]} id={`pass-${k}`}/>
                           <button onClick={() => setShowPassword(p => ({...p, [k]: !p[k]}))} className="px-1">{showPassword[k] ? <EyeOff size={12}/> : <Eye size={12}/>}</button>
                        </div>
                        <button onClick={() => handleAuthUpdate(k, document.getElementById(`pass-${k}`).value)} className="bg-blue-600 text-white w-full mt-1 rounded"><Save size={12} className="mx-auto"/></button>
                     </div>
                  ))}
              </div>
            </div>
            <div className="bg-white p-4 rounded shadow h-96 overflow-y-auto">
              <div className="flex justify-between items-center border-b pb-2 mb-2"><h4 className="font-bold">FAQs</h4><button onClick={addFaq} className="bg-blue-100 text-blue-800 px-2 rounded text-xs font-bold">+ Nueva</button></div>
              {(faqs || []).map(faq => (<div key={faq.id} className="mb-3 border p-2 relative"><button onClick={() => removeFaq(faq.id)} className="absolute top-1 right-1 text-red-500"><X size={12}/></button><input value={faq.q} onChange={e => handleFaqChange(faq.id, 'q', e.target.value)} className="w-full text-xs font-bold mb-1"/><textarea value={faq.a} onChange={e => handleFaqChange(faq.id, 'a', e.target.value)} className="w-full text-xs border rounded p-1 h-12"/><input value={faq.keywords?.join(',')} onChange={e => handleFaqChange(faq.id, 'keywords', e.target.value.split(','))} className="w-full text-[10px] mt-1" placeholder="keywords..."/></div>))}
            </div>
        </div>
      )}
      
      {/* MODAL PARA AGENDAMIENTO MANUAL MEJORADO */}
      {showManualBooking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full relative">
                  <button onClick={() => setShowManualBooking(false)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"><X size={20}/></button>
                  <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2"><UserPlus/> Agendar Paciente (Admin)</h3>
                  
                  <form onSubmit={handleManualBooking} className="space-y-3">
                      <div><label className="text-xs font-bold">Fecha Cita</label><input type="date" name="date" required className="w-full border p-2 rounded" min={getTodayFormatted().split(' ')[0]}/></div>
                      
                      {/* --- NUEVO: SELECCIÓN DE HORA MANUAL --- */}
                      <div className="bg-gray-100 p-2 rounded border border-gray-200">
                          <label className="text-xs font-bold flex items-center gap-1 text-gray-700"><Clock size={12}/> Hora Manual (Opcional)</label>
                          <input type="time" name="manualTime" className="w-full border p-2 rounded bg-white mb-1"/>
                          <p className="text-[10px] text-gray-500">Dejar vacío para cálculo automático (Rellena huecos).</p>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div><label className="text-xs font-bold">Cédula</label><input name="cedula" required className="w-full border p-2 rounded" placeholder="V-12345678"/></div>
                        <div><label className="text-xs font-bold">Teléfono</label><input name="telefono" required className="w-full border p-2 rounded" placeholder="0414..."/></div>
                      </div>
                      <div><label className="text-xs font-bold">Nombre Completo</label><input name="nombre" required className="w-full border p-2 rounded"/></div>
                      <div><label className="text-xs font-bold">Email (Opcional)</label><input name="email" type="email" className="w-full border p-2 rounded"/></div>
                      <div><label className="text-xs font-bold">Tipo</label><select name="tipo" className="w-full border p-2 rounded"><option>Consulta</option><option>Examen</option><option>Control</option></select></div>
                      <div><label className="text-xs font-bold">Motivo</label><input name="motivo" required className="w-full border p-2 rounded"/></div>
                      <button type="submit" className="w-full bg-blue-600 text-white font-bold py-3 rounded hover:bg-blue-700">Guardar Cita</button>
                  </form>
              </div>
          </div>
      )}

      {selectedPatient && <ClinicalHistoryModal patient={selectedPatient} records={clinicalRecords?.[selectedPatient.cedula]} onClose={() => setSelectedPatient(null)} onSave={saveClinicalNote} />}
    </div>
  );
}

export default AdminPanel;